import { useEffect, useSyncExternalStore } from 'react';
import { isNumber } from 'utility/utility';
import { CB_SELECTED_CLIENT_KEY } from '@round/creatorbase-helpers';
import useCurrentUserClients from './useCurrentUserClients';
import useCurrentUserNonAgencyClients from './useCurrentUserNonAgencyClients';

// using default 'storage' event caused local storage to update
const CLIENT_CHANGE_EVENT_NAME = 'creatorbaseClientChange';
const subscribe = (listener: () => void) => {
    window.addEventListener(CLIENT_CHANGE_EVENT_NAME, listener);
    return () => window.removeEventListener(CLIENT_CHANGE_EVENT_NAME, listener);
};

const getSnapshot = () => {
    const id = sessionStorage.getItem(CB_SELECTED_CLIENT_KEY);
    return id ? Number(id) : undefined;
};

const setSelectedClientId = (id: number | null) => {
    if (!id) {
        return;
    }

    window.sessionStorage.setItem(CB_SELECTED_CLIENT_KEY, String(id));
    window.dispatchEvent(
        new CustomEvent(CLIENT_CHANGE_EVENT_NAME, {
            detail: {
                key: CB_SELECTED_CLIENT_KEY,
                newValue: String(id),
            },
        })
    );
};

export default function useClientSelect() {
    const selectedClientId = useSyncExternalStore(subscribe, getSnapshot);

    // This is the creatorbase app, so the default selection must be a non-agency client. Selecting
    // the first one (rather than blindly client_ids[0]) lands crossover users on a sensible client,
    // and corrects a stale or wrong-type id carried over from another app.
    const { clients: nonAgencyClients, status: nonAgencyStatus } = useCurrentUserNonAgencyClients();
    useEffect(() => {
        // Wait only while loading. On a partial fetch error the hook still yields the clients that
        // resolved, so we can pick a default from those rather than leaving nothing selected.
        if (nonAgencyStatus === 'loading') {
            return;
        }

        const isValidSelection = isNumber(selectedClientId) && nonAgencyClients.some((c) => c.id === selectedClientId);
        const firstClientId = nonAgencyClients.length > 0 ? nonAgencyClients[0].id : null;
        if (!isValidSelection && isNumber(firstClientId)) {
            setSelectedClientId(firstClientId);
        }
    }, [nonAgencyStatus, nonAgencyClients, selectedClientId]);

    // Selection is looked up against all clients (agency clients can be selected by crossover users,
    // which triggers a bounce to the agency app rather than an in-app selection).
    const { clients, status, error } = useCurrentUserClients();
    const selectedClient = clients.find((c) => c.id === selectedClientId) ?? null;

    return {
        selectedClientId,
        setSelectedClientId,
        selectedClient,
        clientDataLoadError: error,
        clientDataStatus: status,
    };
}
